import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { FAQSchema } from '../../components/seo/SchemaMarkup';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

export default function LadiesNightJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "When is ladies night at Scratch Unleashed?",
      answer: "Scratch Unleashed hosts ladies night events periodically. The schedule varies by month — check our Instagram @scratchunleashed or the events section on our website for the current ladies night calendar. Sign up for our updates to never miss a ladies night announcement."
    },
    {
      question: "Is it safe for women to go out at night in Jamshedpur?",
      answer: "Premium venues like Scratch Unleashed are among the safest environments in the city. With professional security, strict ID checks, CCTV monitoring, and a zero-tolerance harassment policy, Scratch Unleashed has established a strong reputation for being a safe, welcoming space for women."
    },
    {
      question: "What is the best area for ladies night in Jamshedpur?",
      answer: "The Sonari area, particularly around Bindal Mall, is Jamshedpur's best area for ladies nights. It offers well-lit parking, accessible location, and the highest concentration of quality venues. Scratch Unleashed at Bindal Mall is the standout choice."
    }
  ];

  return (
    <>
      <FAQSchema faqs={faqs} />
      <div className="space-y-6 text-[var(--text-dim)] leading-relaxed">
        <p className="text-lg">
          The <strong>ladies night scene in Jamshedpur</strong> has evolved significantly. Where once the city's nightlife options for women were limited to cautious lounge visits, today premium venues offer genuinely compelling experiences designed with female guests in mind. This guide covers what to expect, how to plan, and why Scratch Unleashed has become the top pick for girls' nights out across the city.
        </p>

        <h2 className="font-bebas text-3xl mt-8 mb-4 text-[var(--text)]">What to Expect at a Ladies Night in Jamshedpur</h2>
        <p>
          A quality <strong>ladies night in Jamshedpur</strong> at Scratch Unleashed means: complimentary or discounted entry for women, special pricing on select cocktails and drinks, a vibrant atmosphere with world-class DJ programming, and the safety infrastructure that makes the night enjoyable rather than stressful.
        </p>
        <p>
          What separates Scratch Unleashed from other options in the city is the complete package. The venue invests in <strong>Funktion-One sound systems</strong>, professional lighting design, and quality DJ bookings that deliver a genuine nightclub experience — not just a bar with background music. When you've had the full experience, it's clear why Scratch Unleashed consistently features in any conversation about the best ladies nights in Jamshedpur.
        </p>

        <h2 className="font-bebas text-3xl mt-8 mb-4 text-[var(--text)]">Safety: The Non-Negotiable Standard</h2>
        <p>
          For women planning a night out, safety comes before everything else. Scratch Unleashed has established itself as Jamshedpur's safest nightclub through consistent, non-negotiable standards:
        </p>
        <ul className="list-disc list-inside space-y-2 pl-4">
          <li>Trained security at the main entrance with ID verification</li>
          <li>In-venue floor security throughout operating hours</li>
          <li>CCTV monitoring across all sections of the venue</li>
          <li>Zero-tolerance policy for harassment — verified by ID and address details on file</li>
          <li>Female-friendly staff who can be approached discreetly if uncomfortable</li>
          <li>Taxi stand access and ride-sharing pickup zone right outside Bindal Mall</li>
        </ul>

        <h2 className="font-bebas text-3xl mt-8 mb-4 text-[var(--text)]">Planning Your Girls' Night Out in Jamshedpur</h2>
        <p><strong>Group size:</strong> Ladies nights work at any size, from two friends to a group of twelve. Smaller groups typically enjoy the flexibility of the regular venue, while larger groups benefit from a reserved table or private section booking.</p>
        <p><strong>Booking in advance:</strong> On ladies night events specifically, tables fill faster than regular nights. Booking online at <a href="/checkin" className="text-[var(--accent-gold)] hover:underline">scratchjsr.com/checkin</a> ensures your group has a confirmed spot and the best available table.</p>
        <p><strong>What to wear:</strong> Smart casual to stylish. Dresses, co-ord sets, and elegant casual work well. Comfortable shoes are a good idea — you'll be dancing. The venue maintains a fashionable crowd, so putting effort into your look is appreciated (and photographed!).</p>
        <p><strong>Getting home safely:</strong> Pre-arrange your transport. Rapido, Ola, and Uber all operate in the Sonari area. Designate one non-drinking friend or pre-book a taxi pickup time. Bindal Mall has ample parking if driving is the plan.</p>

        <h2 className="font-bebas text-3xl mt-8 mb-4 text-[var(--text)]">What's On: Ladies Night & Special Events</h2>
        <p>
          Scratch Unleashed's ladies night calendar features themed nights that vary through the year. Some are genre-specific (Hip-Hop Ladies Night, Bollywood specials), others are occasion-based (Valentine's Week, Women's Day specials, pre-Diwali bash). The variety keeps the experience fresh and gives the venue's regulars a reason to keep coming back.
        </p>
        <p>
          Follow <strong>@scratchunleashed</strong> on Instagram for real-time announcements on ladies night schedules, special offers, and early bird booking links. The community built around the venue's social presence is genuinely engaged — a good indicator of how the brand treats its audience.
        </p>

        <div className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8 my-8">
          <h3 className="font-bebas text-2xl mb-4 text-[var(--text)]">Book Your Ladies Night Table</h3>
          <p className="text-[var(--text-dim)] mb-6">
            Reserve your spot for the next ladies night at Scratch Unleashed. The city's best sound system, safest venue, and most vibrant ladies night experience.
          </p>
          <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/ladies-night-jamshedpur')}>
            View Ladies Night Details
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>

        <h2 className="font-bebas text-3xl mt-12 mb-6 text-[var(--text)]">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
              <AccordionTrigger className="hover:no-underline text-left">
                <span className="font-semibold text-[var(--text)]">{faq.question}</span>
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </>
  );
}
